import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  VStack,
  HStack,
  useToast,
} from '@chakra-ui/react';
import BonusesApi from '../../../../api/bonuses';
import { useCompaniesOptions } from '../../../../hooks/useCompaniesOptions';

// Matches BonusType enum in backend
const BONUS_TYPES = [
  { value: 1, label: 'העברה חינמית' },
  { value: 2, label: 'כל ההעברות חינם' },
];

// Matches BonusCriteriaType enum in backend
const CRITERIA_TYPES = [
  { value: 1, label: 'חברה ספציפית' },
  { value: 2, label: 'מינימום העברות החודש' },
  { value: 3, label: 'כל המשתמשים' },
];

const EMPTY_FORM = {
  name: '',
  startDate: '',
  endDate: '',
  bonusType: '1',
  criteriaType: '',
  companyId: '',
  minApplicationsThisMonth: '',
};

const BonusFormModal = ({ isOpen, onClose, bonus, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const toast = useToast();
  const { companiesOptions } = useCompaniesOptions({ showAll: false });
  const isEditMode = !!bonus;

  useEffect(() => {
    if (bonus) {
      setFormData({
        name: bonus.name || '',
        startDate: bonus.startDate || '',
        endDate: bonus.endDate || '',
        bonusType: String(bonus.bonusType || 1),
        criteriaType: String(bonus.criteriaType || ''),
        companyId: bonus.companyId ? String(bonus.companyId) : '',
        minApplicationsThisMonth: bonus.minApplicationsThisMonth != null
          ? String(bonus.minApplicationsThisMonth)
          : '',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [bonus, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'נא להזין שם', status: 'warning', duration: 2000, isClosable: true });
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      toast({ title: 'נא להזין תאריכים', status: 'warning', duration: 2000, isClosable: true });
      return;
    }
    if (!formData.criteriaType) {
      toast({ title: 'נא לבחור קריטריון', status: 'warning', duration: 2000, isClosable: true });
      return;
    }

    const criteriaType = parseInt(formData.criteriaType);
    if (criteriaType === 1 && !formData.companyId) {
      toast({ title: 'נא לבחור חברה לקריטריון חברה ספציפית', status: 'warning', duration: 2000, isClosable: true });
      return;
    }
    if (criteriaType === 2 && !formData.minApplicationsThisMonth) {
      toast({ title: 'נא להזין מינימום העברות', status: 'warning', duration: 2000, isClosable: true });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
        bonusType: parseInt(formData.bonusType),
        criteriaType,
        companyId: formData.companyId ? parseInt(formData.companyId) : null,
        minApplicationsThisMonth: formData.minApplicationsThisMonth
          ? parseInt(formData.minApplicationsThisMonth)
          : null,
      };

      if (isEditMode) {
        await BonusesApi.updateBonus(bonus.id, payload);
        toast({ title: 'בונוס עודכן בהצלחה', status: 'success', duration: 2000, isClosable: true });
      } else {
        await BonusesApi.createBonus(payload);
        toast({ title: 'בונוס נוצר בהצלחה', status: 'success', duration: 2000, isClosable: true });
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving bonus:', error);
      toast({
        title: isEditMode ? 'שגיאה בעדכון בונוס' : 'שגיאה ביצירת בונוס',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const criteriaType = parseInt(formData.criteriaType);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEditMode ? 'עריכת בונוס' : 'יצירת בונוס חדש'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>שם הבונוס</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="לדוגמה: בונוס מרץ 2026"
              />
            </FormControl>

            <HStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>תאריך התחלה</FormLabel>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>תאריך סיום</FormLabel>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                />
              </FormControl>
            </HStack>

            <FormControl isRequired>
              <FormLabel>סוג בונוס</FormLabel>
              <Select
                value={formData.bonusType}
                onChange={(e) => handleChange('bonusType', e.target.value)}
              >
                {BONUS_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>קריטריון זכאות</FormLabel>
              <Select
                value={formData.criteriaType}
                onChange={(e) => handleChange('criteriaType', e.target.value)}
                placeholder="בחר קריטריון"
              >
                {CRITERIA_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Select>
            </FormControl>

            {(criteriaType === 1 || criteriaType === 2) && (
              <FormControl isRequired={criteriaType === 1}>
                <FormLabel>חברה</FormLabel>
                <Select
                  dir="rtl"
                  value={formData.companyId}
                  onChange={(e) => handleChange('companyId', e.target.value)}
                  placeholder={criteriaType === 2 ? 'כל החברות (אופציונלי)' : 'בחר חברה'}
                >
                  {companiesOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </FormControl>
            )}

            {criteriaType === 2 && (
              <FormControl isRequired>
                <FormLabel>מינימום העברות החודש</FormLabel>
                <Input
                  type="number"
                  min="1"
                  value={formData.minApplicationsThisMonth}
                  onChange={(e) => handleChange('minApplicationsThisMonth', e.target.value)}
                  placeholder="לדוגמה: 3"
                />
              </FormControl>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>ביטול</Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="שומר..."
          >
            {isEditMode ? 'עדכן' : 'צור'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BonusFormModal;
